<template>
  <BaseModal ref="modal">
    <UiModalDialog
      class="cs-field-select-modal max-modal"
      dialog-width="98vw"
      dialog-height="95vh"
    >
      <template #header>
        {{ ls({ ja: '検索項目の選択', en: 'Select search field' }) }}
      </template>
      <div class="jps-modal-content" v-if="ss">
        <div class="search-field-body-select-fields" >
          <SearchBox :ss="ss" no-detail />
          <div
            class="sf-index-types"
            v-if="indexTypeSelection && indexTypes.length > 1"
          >
            <BaseCheckbox
              v-for="it in indexTypes"
              :key="it"
              v-model="indexTypeSelection[it]"
              >{{ indexTypeDisplayFunction(it) }}</BaseCheckbox
            >
          </div>
          <SearchCount :ss="ss"/>

          <UiTable
            v-if="ss.searched"
            :data="ss.state.result.list.filter(item => !(isJps && (
                item.id === 'common.subject' || item.id === 'common.language'
            )))"
            :columns="columns"
          > <!-- JPSでは「主題」「言語」は表示しない -->
            <template v-slot:column-field="{ row }">
              <button
                class="jps-button field-label-button"
                :title="ls(row.label)"
                @click="add(row)"
                :disabled="fieldsEdit.includes(row.id)"
              >
                {{ ls(row.label) }}
              </button>
            </template>
          </UiTable>
          <SearchInfiniteLoading :ss="ss" />
        </div>
      </div>
      <template #footer-extra>
        <div class="search-field-selected">
          <UiDbField
            v-for="f in fieldsEdit"
            class="jps-tag"
            :field="f"
            :key="f"
            :deletable="true"
            @delete="removeFromArray(fieldsEdit, f)"
          />
        </div>
      </template>
      <template #buttons>
        <button @click="modal.reject()">{{ ls(dapLangGlobal.cancel) }}</button>
        <button @click="confirm()" :disabled="!confirmable">{{ ls(dapLangGlobal.ok) }}</button>
      </template>
    </UiModalDialog>
  </BaseModal>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'
import type { BaseModal } from '#components'
import type { ColumnDef } from '~/components/ui/UiTable.vue'

const { ls, lss, led } = useI18n()
const { searchFieldLabel } = useDatabase()
const userStore = useUserStore()
const dataStore = useDataStore()
const { snackbarMsg } = useSnackbar()
const { isJps } = useServiceType();

interface Props {
  fields?: string[],
  database?: string[],
  indexType?: string[],
  queryFilter?: (query: SearchQuery) => SearchQuery
}
const props = withDefaults(defineProps<Props>(), {
})

const modal = ref<InstanceType<typeof BaseModal>>()
const _fields: Ref<string[]> = ref([])
const _database: Ref<string[]> = ref([])
const _indexType: Ref<string[]> = ref([])
const _queryFilter: Ref<(query: SearchQuery) => SearchQuery> = ref()
const ss: Ref<SearchStore<FieldLabel>> = ref(null)
const fieldsEdit: Ref<string[]> = ref([])
const indexTypeSelection: Ref<{ [key: string]: boolean }> = ref(null)
const openAwait = async (options?: Props) => {
  setOptionsValue(_fields, 'fields', props, options, [])
  setOptionsValue(_database, 'database', props, options, [])
  setOptionsValue(_indexType, 'indexType', props, options, [])
  setOptionsValue(_queryFilter, 'queryFilter', props, options)

  fieldsEdit.value = []
  const _indexTypeSelection = {}
  indexTypes.value.forEach((it) => (_indexTypeSelection[it] = true))
  indexTypeSelection.value = _indexTypeSelection

  ss.value = useSearch(
    { keywordFields: ['keywords'] },
    searchFieldLabel,
    {
      configFreeze: false,
      configSyncRoute: false,
      configNoCache: true,
      queryRewriter: (q) => {
        if (userStore.isOrg) {
          q.permission = 'M'
        } else {
          q.permission = 'P'
        }
        if (!q.filter) q.filter = {}

        let indexTypeFilter = indexTypes.value.filter(
          (it) => indexTypeSelection.value[it]
        )
        if (indexTypeFilter.length == 0) indexTypeFilter = ['nomatch']
        q.filter['indexType'] = indexTypeFilter
        if (_database.value) q.filter['database'] = _database.value
        if (_queryFilter.value) return _queryFilter.value(q)
        return q
      }
    }
  )
  if (_fields.value) fieldsEdit.value = clone(_fields.value)
  ss.value.execute()
  return await modal.value.openAwait<string[]>()
}
defineExpose({ openAwait })

const dataTypeDef = INDEX_DATA_TYPE_DEF
const indexTypes = computed(() => {
  if (!_indexType.value) {
    return Object.keys(dataTypeDef.c).filter((dt) => dt != 'none')
  }
  return _indexType.value
})

const indexTypeDisplayFunction = (code: string) => {
  return ls(dataTypeDef.c[code].l)
}

const columns: ColumnDef[] = [
  {
    label: () => lss('項目名', 'Field'),
    key: 'field',
  },
  {
    label: () => lss('データベース', 'Database'),
    path: ['databaseName'],
    display: (label) => ls(label),
  },
  {
    label: () => lss('入力数', 'Filled'),
    path: ['filled'],
  },
  {
    label: () => lss('値の種数', 'Unique'),
    path: ['unique'],
  },
  {
    label: () => lss('タイプ', 'Type'),
    path: ['indexType'],
    display: (e) => led(dataTypeDef, e),
  },
  {
    label: () => lss('例', 'Sample'),
    path: ['sample'],
  },
  {
    label: () => lss('説明', 'Description'),
    path: ['description'],
    display: (label) => ls(label),
  },
]

const confirmable = computed(() => {
  return fieldsEdit.value.length > 0
})

const confirm = () => {
  modal.value.resolve(fieldsEdit.value)
}

const currentIndexType = computed(() => {
  if (fieldsEdit.value.length > 0) {
    return dataStore.field(fieldsEdit.value[0]).indexType
  }
  return null
})

const add = (field: FieldLabel) => {
  if (!fieldsEdit.value.includes(field.id)) {
    if (currentIndexType.value && currentIndexType.value !== field.indexType) {
      snackbarMsg(
        {
          ja: '異なるデータ種別の項目を選択することはできません。',
          en: 'Can not select field with deffierent type',
        }
      )
      return
    }
    if (currentIndexType.value && currentIndexType.value === 'code') {
      if (
        dataStore.field(fieldsEdit.value[0]).codeKey !== field.codeKey
      ) {
        snackbarMsg(
          {
            ja: '異なる種類のコード値を複数選択することはできません。',
            en: 'Can not select field with deffierent code values',
          }
        )
        return
      }
    }
    dataStore.put('field', field.id, field)
    fieldsEdit.value.push(field.id)
  }
}
</script>

<style lang="scss" scoped>
.cs-field-select-modal {
  .sf-index-types {
    width: 100%;
    text-align: center;
  }
  .search-field-selected {
    padding: 1rem;
    overflow-x: auto;
    display: flex;
    .jps-tag:not(:last-child) {
      margin-right: 0.5rem;
    }
  }
  .field-label-button {
    max-width: 16rem;
    min-width: 16rem;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}
</style>
